import React, {useCallback} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {trackPromise, usePromiseTracker} from "react-promise-tracker";
import {Subject} from "rxjs";
import {showToast} from "common";
import _ from "lodash";
import {useObservable} from "rxsupport";
import {ReportModal} from "../../form-components/ReportModal";
import {FatOutlineButton} from "common/component/FatOutlineButton";
import {LoadingSpinner} from "common";
import {InsuranceNotificationInput, AuthorizationInfo} from "../../domain";
import {ModalDatePicker} from "../../form-components/ModalDatePicker";
import {ModalTextAreaInput} from "../../form-components/ModalTextAreaInput";

type Props = {
    visible$: Subject<boolean>;
    authorization: AuthorizationInfo;
    onFinished: (formValues: InsuranceNotificationInput | null) => Promise<void>;
};

export function InsuranceNotificationFormModal({authorization, visible$, onFinished}: Props) {
    const visible = useObservable(visible$, false);

    const methods = useForm<InsuranceNotificationInput>({
        defaultValues: {
            insuranceInactiveDate: "",
            additionalInformation: "",
        }
    });

    const {promiseInProgress} = usePromiseTracker();
    const submitWithTracking = useCallback((formValues: InsuranceNotificationInput | null) => trackPromise(onFinished(formValues)), [onFinished]);

    const onCancel = useCallback(async () => {
        showToast.warning(`Submission for Insurance Notification is cancelled`);
        methods.reset();
        visible$.next(false);
    }, [methods, visible$]);

    const submit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        await methods.handleSubmit(async (data: InsuranceNotificationInput) => {
            await submitWithTracking(_.pick(data, ['insuranceInactiveDate', 'additionalInformation']));
            methods.reset();
            visible$.next(false);
        })(e);
    }, [methods, visible$, submitWithTracking]);

    return (
        <ReportModal visible={visible} title={'Insurance Notification'} onClose={onCancel}>
            <FormProvider {...methods}>
                <form autoComplete="off" onSubmit={submit}>
                    {!promiseInProgress && (
                        <>
                            <div className="space-y-6">
                                <div className="flex flex-col w-full p-4 scrollbar-always"
                                     style={{maxHeight: "calc(100vh - 68px - 100px - 37px - 90px - 60px)"}}>
                                    <ul className="space-y-2">
                                        <li className="w-full flex flex-col text-base py-2 text-black">
                                            <ModalDatePicker<InsuranceNotificationInput>
                                                label="Please provide the date the family is reporting insurance will be inactive *"
                                                disabled={promiseInProgress}
                                                field={'insuranceInactiveDate'}
                                                required
                                                stacked
                                                placeholderText="mm/dd/yyyy"
                                                inputClassName="px-3 py-2"
                                                shouldShowErrorAtBottom={() => true}
                                                bottomError={(msg) => <div className="text-red-700 mt-1">{msg}</div>}
                                                optionalRules={{
                                                    required: {
                                                        value: true,
                                                        message: "Please select the date the family is reporting insurance will be inactive before submitting"
                                                    }
                                                }}
                                            />
                                        </li>
                                        <li className="w-full flex flex-col text-base py-2 text-black">
                                            <ModalTextAreaInput
                                                disabled={promiseInProgress}
                                                field="additionalInformation"
                                                label="Please add any additional relevant information *"
                                                labelPosition="top"
                                                rowsNum={4}
                                                maxLength={4000}
                                                required
                                                requiredMessage="Please add any additional relevant information is a required field."
                                                showCharacterCount
                                            />
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div className="w-full flex border-t dark:border-gray-600 p-4 justify-end">
                                <div className="flex w-1/4">
                                    <FatOutlineButton color={'red'}
                                                      text={"Cancel"}
                                                      onClick={onCancel}/>
                                    <span style={{width: 10}}/>
                                    <FatOutlineButton type="submit" text={"Submit"} color={"green"}/>
                                </div>
                            </div>
                        </>
                    )}

                    {promiseInProgress && (
                        <div className="p-24">
                            <LoadingSpinner text="Processing"/>
                        </div>
                    )}
                </form>
            </FormProvider>
        </ReportModal>
    );
}
